/**
 * Tags the system put on a customer (api/app/collab/tags.py add_auto) — why, in plain Thai, for the customer tab and
 * the chip's tooltip. Owner 27 ก.ย. 69: แท็กแดง "ชำระเงินไม่ผ่าน" also for a visitor who already has a chat
 * (api/app/webchat/paytag.py) — "the staff card should show why the tag was added". Owner 18 ต.ค. 69 "tag อัตโนมัติ":
 * รอชำระ · ลูกค้าเก่า · สนใจ · แพ็ก… · ปัญหา (api/app/collab/autotags.py) — why, and when the system takes it off again.
 */
import type { ContactTagRef } from '../api/types'
import { clockTime, dayKey, shortDate } from './inbox'

const WHY: Record<string, string> = {
  'webchat:question': 'ลูกค้ากดปุ่มคำถามตอนเริ่มแชทบนเว็บ',
  'webchat:ask:payment_failed': 'ลูกค้ากด “มีปัญหาการชำระเงิน แชทกับทีมงาน” ในหน้าคำสั่งซื้อ',
  'webchat:pay_failed': 'หน้าเว็บแจ้งลูกค้าว่าชำระเงินไม่สำเร็จ',
  'webchat:pay_expired': 'หน้าเว็บแจ้งลูกค้าว่าหมดเวลาชำระเงิน',
  'system:hold': 'มีออเดอร์รอชำระ (พักบิล QR หรือลิงก์บัตร)',
  'system:returning:chat': 'เคยสั่งซื้อแล้ว (ออเดอร์จากแชทชำระแล้ว)',
  'system:returning:pos': 'เคยสั่งซื้อแล้ว (ประวัติใน POS)',
  'system:interest': 'ลูกค้าถามราคา แพ็ก เมนู หรือค่าส่ง และยังไม่มีออเดอร์ใน 14 วัน',
  'system:pack:chat': 'แพ็กของออเดอร์ล่าสุด (ลงจากแชท)',
  'system:pack:pos': 'แพ็กของออเดอร์ล่าสุดใน POS',
  'system:problem:chat': 'ลูกค้าแจ้งปัญหาในแชท',
  'system:problem:delivery': 'ระบบติดตามขนส่งพบว่าส่งไม่สำเร็จ หรือถึงช้ากว่ารอบตั้งแต่ 30 นาที',
}

/** When the system takes the tag off by itself — none = it stays until a person takes it off (ลูกค้าเก่า stays). */
const OFF: Record<string, string> = {
  'system:hold': 'ระบบเอาออกเองเมื่อชำระแล้ว หรือยกเลิกพักบิล/ลิงก์',
  'system:interest': 'ระบบเอาออกเองเมื่อลูกค้าสั่งซื้อ',
  'system:pack:chat': 'เปลี่ยนเองเมื่อสั่งแพ็กอื่น',
  'system:pack:pos': 'เปลี่ยนเองเมื่อสั่งแพ็กอื่น',
  'system:problem:chat': 'พนักงานเอาออกเมื่อแก้แล้ว',
  'system:problem:delivery': 'พนักงานเอาออกเมื่อแก้แล้ว',
}

/** Why the system put this tag on (null = a person did). */
export function autoTagWhy(code: string | null | undefined): string | null {
  if (!code) return null
  if (WHY[code]) return WHY[code]!
  if (code.startsWith('webchat:ask:')) return 'ลูกค้าเปิดแชทจากลิงก์บนหน้าเว็บ'
  if (code.startsWith('system:')) return 'ระบบติดให้อัตโนมัติ'
  return code.startsWith('webchat:') ? 'ระบบติดให้จากแชทบนเว็บ' : null
}

/** When the system takes it off again (null = a person takes it off). */
export function autoTagOff(code: string | null | undefined): string | null {
  return (code && OFF[code]) || null
}

/** A tag of the "ลูกค้าเก่า" rule — the customer tab's ลูกค้าเก่า chip already says it. */
export const isReturningAuto = (t: ContactTagRef): boolean => !!t.auto?.startsWith('system:returning')

/** "14:32 น." today, else "26 ก.ย. 14:32 น." (Bangkok). */
export function autoTagWhen(iso: string | null | undefined, nowMs: number): string | null {
  if (!iso || Number.isNaN(Date.parse(iso))) return null
  const hm = `${clockTime(iso)} น.`
  return dayKey(iso) === dayKey(nowMs) ? hm : `${shortDate(iso)} ${hm}`
}

/** Tooltip of a chip: "รอชำระ · ติดอัตโนมัติ: มีออเดอร์รอชำระ (พักบิล QR หรือลิงก์บัตร) · 14:32 น. · ระบบเอาออกเองเมื่อชำระแล้ว หรือยกเลิกพักบิล/ลิงก์"
 *  (null = a person's tag). */
export function autoTagTitle(t: ContactTagRef, nowMs: number): string | null {
  const why = autoTagWhy(t.auto)
  if (!why) return null
  const when = autoTagWhen(t.added_at, nowMs)
  const off = autoTagOff(t.auto)
  return `${t.name} · ติดอัตโนมัติ: ${why}${when ? ` · ${when}` : ''}${off ? ` · ${off}` : ''}`
}

export interface AutoTagNote {
  id: string
  name: string
  tone: ContactTagRef['tone']
  why: string
  when: string | null
  /** When the system takes it off (absent = a person does). */
  off?: string
}

/** The tags on this customer the system put on, with why + when (the customer tab lists them under the chips). */
export function autoTagNotes(tags: ContactTagRef[] | null | undefined, nowMs: number): AutoTagNote[] {
  return (tags ?? []).flatMap((t) => {
    const why = autoTagWhy(t.auto)
    const off = autoTagOff(t.auto)
    return why ? [{ id: t.id, name: t.name, tone: t.tone, why, when: autoTagWhen(t.added_at, nowMs), ...(off ? { off } : {}) }] : []
  })
}
